import {useState} from 'react'
import {renderSignToPDF, downloadPDF, slug, withExtension} from '../../../lib/sign-export'
import type {SignDef, SignInput} from '../../../lib/sign-catalog'
import {lvsRef} from '../../../lib/utils'
import {MonoLabel} from '../../ui/mono-label'
import {UnderlineInput} from '../../ui/underline-field'
import {WarningPill} from '../../ui/warning-pill'

export interface ExportStepProps {
    readonly def: SignDef
    readonly input: SignInput
}

function defaultFilename(def: SignDef, input: SignInput): string {
    const firstText = input.rows[0]?.text ?? ''
    const base = `LVS-${def.code}${firstText ? `-${slug(firstText)}` : ''}`
    return `${base}.pdf`
}

export function ExportStep({def, input}: ExportStepProps) {
    const [filename, setFilename] = useState(() => defaultFilename(def, input))
    const [pdfPending, setPdfPending] = useState(false)
    const [pdfError, setPdfError] = useState<string | null>(null)

    const downloadAsPdf = async () => {
        setPdfPending(true)
        setPdfError(null)
        try {
            const blob = await renderSignToPDF(def, input)
            downloadPDF(withExtension(filename), blob)
        } catch (e) {
            setPdfError(e instanceof Error ? e.message : 'PDF eksports neizdevās')
        } finally {
            setPdfPending(false)
        }
    }

    return (
        <section className='space-y-5 max-w-xl'>
            <div className='flex flex-col gap-1.5'>
                <MonoLabel>Faila nosaukums</MonoLabel>
                <UnderlineInput
                    type='text'
                    value={filename}
                    onChange={e => setFilename(e.target.value)}
                />
            </div>

            <div className='flex flex-wrap gap-3'>
                <button
                    type='button'
                    onClick={downloadAsPdf}
                    disabled={pdfPending}
                    className='font-mono text-sm tracking-[0.2em] uppercase px-5 py-2.5 bg-primary text-on-primary hover:bg-primary/90 disabled:opacity-50'
                >
                    {pdfPending ? 'Sagatavo PDF…' : 'Lejupielādēt PDF'}
                </button>
            </div>

            {pdfError && <WarningPill>{pdfError}</WarningPill>}

            <p className='font-mono text-[11px] tracking-[0.15em] uppercase text-foreground/40'>
                Fails ir mm-precīzs, atbilst LVS 77-3:2016{' '}
                {def.lvsRefs.map(r => lvsRef(r, 'clause')).join(', ')}
            </p>
        </section>
    )
}
